import { useMemo } from 'react';
import { css } from '@emotion/react';
import { useSubNavStore, type SubNavStore, type NavItem } from '@frontend/nav-primitives';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useWeTags } from '@frontend/scope';
import { useLocation } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Heading, type HeadingProps } from '@frontend/design-system';
import { NavItemNotificationBadge, HeaderSubNavigation } from './navigation';

type HeaderProps = Omit<HeadingProps, 'title' | 'children'> & {
  title: string | ((activePath?: NavItem['subNavItems'][number]) => React.ReactNode);
};

const headerStyles = css`
  display: flex;
  align-items: center;
  padding: ${theme.spacing(2, 2, 1, 2)};
  justify-content: space-between;

  @container (max-width:  ${breakpoints.small.max}px) {
    font-size: ${theme.fontSize(24)};
  }

  @media (max-width: ${breakpoints.small.max}px) {
    font-size: ${theme.fontSize(24)};
  }
`;

const selectNavItem = (currentRoot: string) => (state: SubNavStore) => state.parentNavData?.[currentRoot];

const hasSameSubPath = (a: string, b: string): boolean => {
  const aPath = a.split('/');
  const bPath = b.split('/');
  return aPath[2] === bPath[1];
};

export const Header = ({ title, ...rest }: HeaderProps) => {
  const isSharedHeaderEnabled = useWeTags('shared-header');
  const currentPath = useLocation().pathname;
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  const currentRoot = useMemo(() => {
    const [, ...pathParts] = currentPath?.split('/') ?? [];
    return `/${pathParts?.[0] ?? ''}`;
  }, [currentPath]);

  const navItem = useSubNavStore(useMemo(() => selectNavItem(currentRoot), [currentRoot]));

  const activePath = useMemo(
    () => navItem?.subNavItems?.find((item) => hasSameSubPath(currentPath, item?.relativePath ?? '')),
    [navItem?.subNavItems, currentPath]
  );

  const displayTitle = useMemo(() => {
    if (typeof title === 'function') {
      return title(activePath);
    } else if (typeof title === 'string') {
      return title;
    } else {
      return activePath?.label;
    }
  }, [title, activePath]);

  if (!isSharedHeaderEnabled) {
    return (
      <Heading
        level={1}
        {...rest}
        css={css`
          @container (max-width:  ${breakpoints.small.max}px) {
            font-size: ${theme.fontSize(24)};
          }

          @media (max-width: ${breakpoints.small.max}px) {
            font-size: ${theme.fontSize(24)};
          }
        `}
      >
        <span>{displayTitle}</span>
      </Heading>
    );
  }

  return (
    <Heading
      level={1}
      {...rest}
      css={css`
        @container (max-width:  ${breakpoints.small.max}px) {
          font-size: ${theme.fontSize(24)};
        }

        @media (max-width: ${breakpoints.small.max}px) {
          font-size: ${theme.fontSize(24)};
        }
      `}
    >
      {isMobile ? (
        <HeaderSubNavigation
          root={currentRoot}
          activePath={activePath?.relativePath ?? ''}
          subNavItems={navItem?.subNavItems}
        >
          <span
            css={css`
              text-align: ${rest?.textAlign ?? 'left'};
              font-size: ${theme.font.size.h2};
            `}
          >
            {displayTitle}
          </span>
          <NavItemNotificationBadge count={activePath?.count} isPassiveCount={activePath?.isPassiveCount} />
        </HeaderSubNavigation>
      ) : (
        <span
          css={css`
            text-align: ${rest?.textAlign ?? 'left'};
            font-size: ${theme.font.size.h2};
          `}
        >
          {displayTitle}
        </span>
      )}
    </Heading>
  );
};

export const PanelHeader = ({ children, ...rest }: React.PropsWithChildren<HeaderProps>) => (
  <header css={headerStyles}>
    <Header {...rest} />
    {children}
  </header>
);
